import React, { useEffect, useState } from "react";
import { Client } from "../../interfaces/client";
import { Order } from "../../interfaces/order";
import { CriminalOrder } from "../../interfaces/criminalOrder";
import { ReferenceOrder } from "../../interfaces/referenceOrder";
import { ReferenceDetail } from "../../interfaces/referenceDetaill";
import { CriminalDetail } from "../../interfaces/criminalDetail";
import {
  generateCriminalFinalReport,
  sendCriminalFinalReport,
  sendCriminalFinalReportSuran,
} from "../../services/criminalOrderService";
import {
  generateReferenceFinalReport,
  sendReferenceFinalReport,
} from "../../services/referenceOrderService";
import { evaluateOrderStatus } from "../../services/orderService";

interface SendFinalReportPopupProps {
  client: Client;
  order: Order;
  criminalOrder?: CriminalOrder;
  referenceOrder?: ReferenceOrder;
  criminalDetails?: CriminalDetail[];
  referenceDetails?: ReferenceDetail[];
  onClose: (message?: string) => void;
}

const SendFinalReportPopup: React.FC<SendFinalReportPopupProps> = ({
  client,
  order,
  criminalOrder,
  referenceOrder,
  criminalDetails,
  referenceDetails,
  onClose,
}) => {
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [byMail, setByMail] = useState(false);
  const [pdfCheck, setPdfCheck] = useState(false);

  const DELIVERY_OPTION_MAP: Record<number, string> = {
    1: "Email",
    2: "Fax",
    3: "Mail",
  };

  useEffect(() => {
    const generateFinalReport = async () => {
      setIsSending(true);
      setErrorMessage(null);
      try {
        setLoadingSpinner(true);
        if (criminalOrder && criminalDetails) {
          const responseUrl = await generateCriminalFinalReport(
            client,
            order,
            criminalOrder,
            criminalDetails,
          );
          if (responseUrl) {
            setPdfBlob(responseUrl);
            const pdfUrl = URL.createObjectURL(responseUrl);
            setPdfUrl(pdfUrl);
          } else {
            throw new Error("Failed to generate the report.");
          }
          //check if motor/credit have pdfs and red flags have pdfs
          const pdfCheckResult = criminalDetails.some(
            (detail) =>
              (detail.serviceID === 12 ||
                detail.serviceID === 16 ||
                detail.serviceID === 34 ||
                detail.redFlag === true) &&
              (!detail.receivedFormUrl ||
                detail.receivedFormUrl.trim() === "") &&
              detail.inActive !== true,
          );
          setPdfCheck(pdfCheckResult);

          if (pdfCheckResult) {
            setErrorMessage(
              "A Credit, Motor, Social Media, or Red Flag criminal detail(s) is missing a pdf attachment.",
            );
          }
        } else if (referenceOrder && referenceDetails) {
          const responseUrl = await generateReferenceFinalReport(
            client,
            order,
            referenceOrder,
            referenceDetails,
          );
          if (responseUrl) {
            setPdfBlob(responseUrl);
            const pdfUrl = URL.createObjectURL(responseUrl);
            setPdfUrl(pdfUrl);
          } else {
            throw new Error("Failed to generate the report.");
          }
        }

        const mailPreference = client.deliveryOptionID === 3 ? true : false;
        setByMail(mailPreference);
      } catch (error) {
        setErrorMessage("Error while generating the final report.");
      } finally {
        setIsSending(false);
        setLoadingSpinner(false);
      }
    };

    generateFinalReport();
  }, [
    client,
    criminalDetails,
    criminalOrder,
    order,
    referenceDetails,
    referenceOrder,
  ]);

  const sendFinalReport = async () => {
    if (!pdfBlob) {
      setErrorMessage("Please generate the report before sending.");
      return;
    }
    try {
      setLoadingSpinner(true);
      let response;
      if (criminalOrder) {
        if (client.abbreviation === "784" || client.abbreviation === "785") {
          response = await sendCriminalFinalReportSuran(
            client,
            criminalOrder,
            pdfBlob,
            byMail,
          );
        } else {
          response = await sendCriminalFinalReport(
            client,
            criminalOrder,
            pdfBlob,
            byMail,
          );
        }
      } else if (referenceOrder) {
        response = await sendReferenceFinalReport(
          client,
          referenceOrder,
          pdfBlob,
          byMail,
        );
      }

      // set order as closed or subscription status
      await evaluateOrderStatus(order.orderID);

      if (response) {
        onClose();
      } else {
        throw new Error("Failed to send the final report.");
      }
    } catch (error) {
      setErrorMessage("Failed to send the final report.");
    } finally {
      setLoadingSpinner(false);
      setIsSending(false);
    }
  };

  return (
    <>
      <div className="background-mask" onClick={() => onClose()}></div>
      <div className="filter-popup">
        <div className="close-button-container">
          <button className="close-button" onClick={() => onClose()}>
            <i className="bi bi-x-circle"></i>
          </button>
        </div>
        <div className="filter-header">
          <h2>Send {criminalOrder ? "Criminal" : "Reference"} Final Report</h2>
          <div>
            The Generated Report is a preview of the pdf that will be made
            available to the client. Email(s) will be sent to client.
          </div>
          <br></br>
          {(criminalOrder?.finalReportUrl ||
            referenceOrder?.finalReportUrl) && (
            <div className="error">
              You will be replacing the existing final report. Save the initial
              download to the candidate’s details if you need to keep a record.
            </div>
          )}
        </div>
        <br></br>
        <div className="dialog-container">
          <div className="field-long" style={{ flex: "0 0 45%" }}>
            <label className="field-label">Client Name:</label>
            <div className="field-input">
              <input
                className="form-control"
                type="text"
                value={client.clientName}
                disabled
              />
            </div>
          </div>
          <div className="field-long" style={{ flex: "0 0 100%" }}>
            <label className="field-label">Generated Report:</label>
            <div className="field-input">
              {pdfUrl ? (
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                  View Report{" "}
                  <i className="btn-icon bi bi-box-arrow-up-right"></i>
                </a>
              ) : (
                <p>Generating report...</p>
              )}
            </div>
          </div>
          <div className="field-long" style={{ flex: "0 0 100%" }}>
            <label className="field-label">Send By Mail? :</label>
            <div>
              <input
                className="form-check-input"
                type="checkbox"
                checked={byMail}
                onChange={() => setByMail(!byMail)}
              />
            </div>
          </div>
          <div style={{ fontSize: "small" }}>
            Client prefers reports by{" "}
            <b> {DELIVERY_OPTION_MAP[client.deliveryOptionID ?? 3]} </b>
          </div>
          {byMail && (
            <div style={{ paddingTop: "10px" }}>
              <div style={{ fontSize: "small" }}>
                Admin is in charge of mailing report. 'Send Report' will
                complete the {criminalOrder ? "Criminal" : "Reference"} Order
                and add a Final Report Date. Final Report PDF will be downloaded
                to the browser.{" "}
                {criminalOrder ? "It will also now be invoiced." : ""}
              </div>
            </div>
          )}
          {errorMessage && <div className="error">{errorMessage}</div>}
        </div>
        <div className="filter-actions">
          <button
            className={`smbtn btn-green ${!pdfUrl || isSending || pdfCheck ? "disabled" : ""}`}
            onClick={sendFinalReport}
            disabled={!pdfUrl || isSending || pdfCheck}
          >
            Send Report
          </button>
        </div>
      </div>
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </>
  );
};

export default SendFinalReportPopup;
